import { useEffect, useMemo, useCallback, useState, lazy, Suspense } from 'react'
import { useNavigate } from 'react-router-dom'
import { usePuzzle } from '../../context/PuzzleContext'
import { useLanguage } from '@context/LanguageContext'
import { useLayoutVisibility } from '@context/LayoutContext'
import { useAnimations } from '@context/AnimationsContext'
import { PUZZLE_IMAGE_PATHS, DIFFICULTY_TO_SIZE, PUZZLE_TIMINGS } from '../../constants/puzzle'
import BoardStage from '../ui/BoardStage'
import PuzzleBoard from '../ui/PuzzleBoard'
import CompletionOverlay from '../ui/CompletionOverlay'
import PuzzleEndModal from '../ui/PuzzleEndModal'
import { cx } from '@utils/cx'
import styles from './PuzzleEnd.module.css'

// Lazy imports for heavy components
const Confetti = lazy(() => import('@shared/components/effects/Confetti.jsx'))

export default function PuzzleEnd ({ onRestart }) {
  useLayoutVisibility({ header: false, footer: false })

  const { tFunc, lang } = useLanguage()
  const { difficulty, gameSnapshot, exitToStart } = usePuzzle()
  const { animationsEnabled } = useAnimations()
  const navigate = useNavigate()
  const imageSrc = gameSnapshot?.imageSrc ?? PUZZLE_IMAGE_PATHS.cover
  const imageDescription = useMemo(() => {
    if (gameSnapshot?.metadata?.descriptionKey) {
      return tFunc(gameSnapshot.metadata.descriptionKey)
    }
    return tFunc('puzzle.images.default')
  }, [gameSnapshot, tFunc])

  const imageAlt = useMemo(() => {
    const alt = tFunc('puzzle.alt')
    return alt.replace('{description}', imageDescription)
  }, [imageDescription, tFunc])

  // States for user interaction
  const [showCelebration, setShowCelebration] = useState(false)
  const [showConfetti, setShowConfetti] = useState(false)
  const [currentStep, setCurrentStep] = useState(0)
  const [showModal, setShowModal] = useState(false)

  const size = difficulty
    ? (DIFFICULTY_TO_SIZE[difficulty] || DIFFICULTY_TO_SIZE.easy)
    : DIFFICULTY_TO_SIZE.easy

  // Create solved board state (tiles in correct positions 0,1,2,3...n-2, empty and end)
  const solvedBoardState = useMemo(() => {
    const totalTiles = size * size
    return Array.from({ length: totalTiles }, (_, i) => i)
  }, [size])

  // Progressive text by steps
  const textSteps = useMemo(() => {
    const step1 = tFunc('puzzle.end.step1')
    const step2Template = tFunc('puzzle.end.step2')
    const step2 = step2Template.replace('{description}', imageDescription)
    const step3 = tFunc('puzzle.end.step3')

    return [step1, step2, step3]
  }, [tFunc, imageDescription])

  // Initialize celebration on mount
  useEffect(() => {
    if (!animationsEnabled) {
      // Reduce motion is enabled: skip animation
      setShowCelebration(true)
      return
    }

    // With animations: start glow + fade
    const celebrationTimer = setTimeout(() => {
      setShowCelebration(true)
      setShowConfetti(true)
    }, PUZZLE_TIMINGS.celebrationStart)

    return () => clearTimeout(celebrationTimer)
  }, [animationsEnabled])

  // Handler for user interaction text progression
  const handleNextStep = () => {
    if (currentStep < 2) {
      setCurrentStep(prev => prev + 1)
    } else {
      setShowModal(true)
    }
  }

  const handlePlayAgain = useCallback(() => {
    setShowModal(false)
    if (typeof onRestart === 'function') onRestart()
  }, [onRestart])

  const handleGoHome = useCallback(() => {
    setShowModal(false)
    exitToStart()
    navigate(`/${lang}`)
  }, [exitToStart, navigate, lang])

  return (
    <section
      className={cx(
        styles.puzzleEnd, showCelebration && styles.celebrating)}
      role='region'
      aria-label={tFunc('puzzle.access.endArea')}
    >
      {/* Header /title (reserves same space as Game) */}
      <header className={styles.topbar}>
        <h1 className={styles.heading}>
          {tFunc('puzzle.end.title')}
        </h1>
      </header>

      {/* Board Stage - Same position as Game */}
      <BoardStage size={size}>
        {/* Puzzle Board in solved state (non-interactive) */}
        <PuzzleBoard
          board={solvedBoardState}
          size={size}
          imageSrc={imageSrc}
          interactive={false}
          inputBlocked
        />

        {/* Completion overlay with fade-in effect */}
        <CompletionOverlay
          imageSrc={imageSrc}
          alt={imageAlt}
          show={showCelebration}
          delay={500}
          duration={PUZZLE_TIMINGS.overlayFadeDuration}
        />
      </BoardStage>

      {/* Progressive text by user interaction */}
      {currentStep < textSteps.length && (
        <div className={styles.content}>
          <div className={styles.textContainer}>
            <p id='puzzle-end-step' className={styles.stepText}>
              {textSteps[currentStep]}
            </p>

            <button
              type='button'
              className={styles.nextBtn}
              onClick={handleNextStep}
              aria-describedby='puzzle-end-step'
              aria-label={tFunc('puzzle.access.nextStep')}
            >
              {tFunc('puzzle.end.continue')}
            </button>
          </div>
        </div>
      )}

      {/* Screen reader announcement */}
      <div className='sr-only' aria-live='polite'>
        {showCelebration ? tFunc('puzzle.access.victoryAnnouncement') : ''}
      </div>

      {/* Confetti (PRM-aware) */}
      {showConfetti && animationsEnabled && (
        <Suspense fallback={null}>
          <Confetti />
        </Suspense>
      )}

      {/* Final modal */}
      {showModal && (
        <PuzzleEndModal
          onPlayAgain={handlePlayAgain}
          onGoHome={handleGoHome}
        />
      )}
    </section>
  )
}
